import React, {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {Button, Table, TableBody, TableWrapper} from '../../../../../../_gori/partials/widgets'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import SettingsService from '../../../core/_requests'
import {ManagerDetail, ManagerListItem} from '../../../core/_types'
import {getManagerName} from './_helpers'

const DEFAULT_MANAGER_DETAIL: ManagerDetail = {
  accounts: [],
  manager: {id: 0, email: '', first_name: '', last_name: ''},
  branch: {id: 0, name: ''},
}

type Props = {
  show: boolean
  handleClose: () => void
  manager?: ManagerListItem | null
}

const AccountsModal: React.FC<Props> = ({show, handleClose, manager = null}) => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const [managerDetail, setManagerDetail] = useState<ManagerDetail>(DEFAULT_MANAGER_DETAIL)

  useEffect(() => {
    if (!show || !manager?.branch?.id) {
      setManagerDetail(DEFAULT_MANAGER_DETAIL)
      return
    }

    let cancelled = false
    const fetchManagerDetail = async () => {
      try {
        const response = await SettingsService.getManagerDetail(manager.branch?.id, {
          cancelToken: newCancelToken(),
        })
        if (!cancelled) {
          setManagerDetail({
            manager: response.manager,
            branch: response.branch,
            accounts: response.accounts ?? [],
            accounts_count: response.accounts_count ?? response.accounts?.length ?? 0,
          })
        }
      } catch (error) {
        if (isCancel(error)) return
        if (!cancelled) {
          setManagerDetail(DEFAULT_MANAGER_DETAIL)
        }
      }
    }
    fetchManagerDetail()

    return () => {
      cancelled = true
    }
  }, [show, manager, newCancelToken, isCancel])

  const accounts = managerDetail.accounts

  const columns = useMemo(
    () => [
      {
        id: 'account_name',
        Header: intl.formatMessage({id: 'ACCOUNT_NAME'}),
        headerClassName: 'min-w-125px',
        cellClassName: 'text-dark',
        Cell: ({row}: {row: any}) => <div>{row.original.name}</div>,
      },
      {
        id: 'account_id',
        Header: intl.formatMessage({id: 'ACCOUNT_ID'}),
        headerClassName: 'min-w-125px text-start',
        cellClassName: 'text-dark text-start',
        Cell: ({row}: {row: any}) => <div>{row.original.id}</div>,
      },
      {
        id: 'status',
        Header: intl.formatMessage({id: 'STATUS'}),
        headerClassName: 'w-150px text-center',
        cellClassName: 'w-150px text-center',
        Cell: ({row}: {row: any}) => (
          <span
            className={`account-status-badge-${row.original?.status?.label
              ?.replaceAll('_', '-')
              ?.toLowerCase()} px-4 py-2 rounded-pill w-100 d-flex justify-content-center`}
          >
            {intl.formatMessage({id: row.original.status?.label})}
          </span>
        ),
      },
    ],
    [intl]
  )

  return (
    <Modal
      id='gori_modal_manager_accounts'
      tabIndex={-1}
      centered
      dialogClassName='modal-size-md h-auto'
      contentClassName='modal-padding'
      show={show}
      backdrop='static'
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <Modal.Title bsPrefix='fw-bolder fs-1 modal-title'>
          {getManagerName(managerDetail.manager)}
          {managerDetail.branch && (
            <span className='d-block fs-5 fw-normal text-gray-600 mt-2 fw-bolder'>{`${
              managerDetail.branch?.name
            } - ${managerDetail.accounts_count ?? accounts.length} ${intl.formatMessage({
              id: 'ACCOUNTS',
            })}`}</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='overflow-auto p-0'>
        <TableWrapper className='border-0 shadow-none' fullWidth={false}>
          <TableBody>
            <Table columns={columns} data={accounts} tbodyClass='text-gray-600 fw-bold' />
          </TableBody>
        </TableWrapper>
      </Modal.Body>
      <Modal.Footer>
        <div className='d-flex justify-content-end w-100'>
          <Button
            className='btn btn-advanced-secondary'
            label={intl.formatMessage({id: 'CLOSE'})}
            loadingText={intl.formatMessage({id: 'CLOSE'})}
            event={handleClose}
          />
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export {AccountsModal}
